/**
 * Analytics consent for UK/EU visitors (2026-10-08).
 *
 * The founder advertises in the UK and EU, where the cookie rules (PECR /
 * ePrivacy) expect a choice BEFORE non-essential analytics storage is set.
 * So, for visitors in those regions, PostHog starts in a cookie-less,
 * replay-less mode (lib/posthog.ts) until they answer the banner
 * (components/AnalyticsConsentBanner.tsx). Everyone else is unchanged.
 *
 * Region heuristic: the DEVICE TIME ZONE, not IP geolocation — we have no
 * geo lookup, and the time zone is available instantly and offline.
 *   - every "Europe/*" zone (this over-includes a few non-EEA countries such
 *     as Switzerland, Turkey and Ukraine — showing them a polite banner is
 *     harmless),
 *   - the Atlantic islands that are part of the EEA (Canary Islands, Madeira,
 *     Azores, Iceland) plus the Faroe Islands and Svalbard (Danish/Norwegian
 *     rules), and the legacy alias names some devices still report.
 *   - an UNKNOWN time zone counts as "ask" — the privacy-safe default.
 * EU outermost regions in other oceans (Réunion, Guadeloupe…) are not
 * covered; nobody is advertised to there. A UK/EU person whose phone is set
 * to a non-European zone (travelling) won't see the banner, and can still
 * choose from the Privacy page ("Cookie settings").
 *
 * The stored choice is the source of truth once made, in every region.
 */

export type ConsentChoice = "granted" | "denied";

/** How PostHog should run right now. */
export type AnalyticsMode =
  /** Normal: cookies/localStorage, autocapture, session replay. */
  | "full"
  /** UK/EU visitor who hasn't chosen: in-memory id, no autocapture, no replay. */
  | "pending"
  /** Declined: in-memory id and capturing switched off. */
  | "denied";

export const CONSENT_KEY = "trybeup_analytics_consent";

/** Fired when the Privacy page's "Cookie settings" clears the choice — the
 *  banner listens and re-opens, in every region. */
export const CONSENT_REOPEN_EVENT = "trybeup:analytics-consent-reopen";

const EEA_ATLANTIC = new Set([
  "Atlantic/Canary",
  "Atlantic/Madeira",
  "Atlantic/Azores",
  "Atlantic/Reykjavik",
  "Atlantic/Faroe",
  "Atlantic/Faeroe",
  "Arctic/Longyearbyen",
]);

/** Legacy IANA aliases for European zones (older Android builds report these). */
const EUROPE_ALIASES = new Set([
  "GB", "GB-Eire", "Eire", "Iceland", "Portugal", "Poland",
  "WET", "CET", "MET", "EET",
]);

/** Pure: does this time zone put the device in the UK/EU consent region? */
export function isConsentRegion(timeZone: string | null | undefined): boolean {
  if (!timeZone) return true; // unknown → ask (privacy-safe)
  if (timeZone.startsWith("Europe/")) return true;
  return EEA_ATLANTIC.has(timeZone) || EUROPE_ALIASES.has(timeZone);
}

export function deviceTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export function readConsent(): ConsentChoice | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

export function writeConsent(choice: ConsentChoice): void {
  try { localStorage.setItem(CONSENT_KEY, choice); } catch { /* private mode — applies to this visit only */ }
}

export function clearConsent(): void {
  try { localStorage.removeItem(CONSENT_KEY); } catch { /* ignore */ }
}

/** Privacy → Cookie settings: show the banner again. The stored choice
 *  stays until Accept or Decline replaces it — clearing it first silently
 *  turned a Decline outside the UK/EU back into "on" (no banner there on the
 *  next load) and let the Meta Pixel start without an answer (review
 *  2026-10-09). */
export function reopenConsentChoice(): void {
  try { window.dispatchEvent(new Event(CONSENT_REOPEN_EVENT)); } catch { /* no window */ }
}

/** Pure: which PostHog mode a visitor gets. */
export function analyticsMode(choice: ConsentChoice | null, consentRegion: boolean): AnalyticsMode {
  if (choice === "granted") return "full";
  if (choice === "denied") return "denied";
  return consentRegion ? "pending" : "full";
}

/** The mode for this device, now. */
export function currentAnalyticsMode(): AnalyticsMode {
  return analyticsMode(readConsent(), isConsentRegion(deviceTimeZone()));
}

/** Inside an iframe (an embed, a design preview) — never show the banner there. */
export function isEmbedded(): boolean {
  try {
    return typeof window !== "undefined" && window.self !== window.top;
  } catch {
    return true; // cross-origin parent blocks the read → we're framed
  }
}

/** Pure: should the consent banner be on screen? */
export function shouldShowConsentBanner(opts: {
  choice: ConsentChoice | null;
  consentRegion: boolean;
  embedded: boolean;
  /** The person asked to change their choice (Privacy → Cookie settings). */
  reopened?: boolean;
}): boolean {
  if (opts.embedded) return false;
  if (opts.reopened) return true;
  return opts.choice === null && opts.consentRegion;
}
